'use client'

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperInstance } from 'swiper';

import 'swiper/css';
import "./some.css"
import { Keyboard, Mousewheel, Pagination, Navigation } from "swiper/modules";
import 'swiper/css/mousewheel'
import BookDetails from '../(book-details)/BookDetail/BookDetail';
import BookTransition from '../BookTransition/BookTransition';
import { BookInterface } from '../books/types/book';
import { useBookData} from '../books/utils/imageLoaders';
import { track } from '@vercel/analytics';
import { useBodyClass, useIsClient } from '@/hooks/useBrowser';

interface BookCarouselProps {
  books: BookInterface[];
  activeBook: BookInterface | null;
  onActiveBookChange: (book: BookInterface) => void;
  isDetailsOpen?: boolean;
  onToggleDetails?: () => void;

}

interface ExtendedSwiper extends SwiperInstance {
  preciseNavigation?: {
    startX: number;
    startY: number;
    timestamp: number;
  }
}

const MobileBookCarousel = ({ books, activeBook, onActiveBookChange,  
  isDetailsOpen = false,
  onToggleDetails }: BookCarouselProps) => {

  const [internalIsDetailsOpen, setInternalIsDetailsOpen] = useState(isDetailsOpen);
  const swiperRef = useRef<SwiperInstance | null>(null);
    const { bookHeights, spineColours } = useBookData(books, 172, true);
  
  // Flag to track if the active book was just set via slide change
  const [recentlyChanged, setRecentlyChanged] = useState(false);
  const isClient = useIsClient()

  useBodyClass('book-details-open', isDetailsOpen)
 
  // Precise touch navigation handler
  const handleTouchStart = useCallback((swiper: ExtendedSwiper, event: TouchEvent) => {
    swiper.preciseNavigation = {
      startX: event.touches[0].clientX,
      startY: event.touches[0].clientY,
      timestamp: Date.now()
    };
  }, []);
  const handleToggleDetails = () => {
    if (onToggleDetails) {
      onToggleDetails();
    } else {
      setInternalIsDetailsOpen(!internalIsDetailsOpen);
    }
  };

  const handleTouchEnd = useCallback((swiper: ExtendedSwiper, event: TouchEvent) => {
    if (!swiper.preciseNavigation) return;

    const { startX, timestamp } = swiper.preciseNavigation;
    const endX = event.changedTouches[0].clientX;
    const diffX = endX - startX;
    const timeDiff = Date.now() - timestamp;

    // Strict conditions for slide change
    const isQuickSwipe = timeDiff < 250; // Fast swipe
    const isSignificantSwipe = Math.abs(diffX) > 50; // Minimum distance

    if ((isQuickSwipe && isSignificantSwipe) || Math.abs(diffX) > 100) {
      // Precisely move one slide in the swipe direction
      diffX > 0 ? swiper.slidePrev() : swiper.slideNext();
    }

    // Reset
    swiper.preciseNavigation = undefined;
  }, []);

  // Click handler with protection against immediate opening
  const handleBookClick = (isActive: boolean) => {
    if (isActive) {
      if (!recentlyChanged) {
        if (onToggleDetails) {
          onToggleDetails();
        } else {
          setInternalIsDetailsOpen(true);
        }
        track("book details opened");
      }
    }
  };
  useEffect(() => {
    if (onToggleDetails) {
      setInternalIsDetailsOpen(isDetailsOpen);
    }
  }, [isDetailsOpen, onToggleDetails]);

  useEffect(() => {
    if(!isClient) return;

    const swiper = swiperRef.current;
    if (!swiper) return;

    const swiperEl = swiper.el;
   

    // Add passive event listeners
    const touchStartHandler = (event: TouchEvent) => 
      handleTouchStart(swiper as ExtendedSwiper, event);
    const touchEndHandler = (event: TouchEvent) => 
      handleTouchEnd(swiper as ExtendedSwiper, event);

    swiperEl.addEventListener('touchstart', touchStartHandler, { passive: true });
    swiperEl.addEventListener('touchend', touchEndHandler, { passive: true });

    // Cleanup
    return () => {
      swiperEl.removeEventListener('touchstart', touchStartHandler);
      swiperEl.removeEventListener('touchend', touchEndHandler);
    };
  }, [handleTouchStart, handleTouchEnd, isClient]);

  return (
    <>
      <div className="container">
        <Swiper
          modules={[Keyboard, Mousewheel, Pagination, Navigation]}
          loop={true}
          slidesPerView={10}
          centeredSlides={true}
          slideToClickedSlide={true}
          spaceBetween={16}
          initialSlide={0}
          followFinger={false}    // Prevent default following
          allowTouchMove={true}   // Allow custom touch handling
          touchRatio={0}          // Disable default touch behavior
          simulateTouch={false}   // Prevent simulated touch
          preventInteractionOnTransition={true}
          speed={300}             // Transition speed
          watchSlidesProgress={true}
          
          mousewheel={{
            forceToAxis: true,
            sensitivity: 1,
            releaseOnEdges: false
          }}
          keyboard={{
            enabled: true,
            onlyInViewport: true
          }}
          pagination={{
            dynamicBullets: true,
          }}
          className="mySwiper"
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          onSlideChangeTransitionStart={() => {
            setRecentlyChanged(true);
          }}
          onSlideChangeTransitionEnd={() => {
            // Short delay before allowing details to open
            setTimeout(() => setRecentlyChanged(false), 100);
          }}
          onSlideChange={(swiper) => {
            const newActiveBook = books[swiper.realIndex];
            onActiveBookChange(newActiveBook);
          }}>
          {books.map((book, index) => (
            <SwiperSlide key={index}>
              {({ isActive }) => (
                <BookTransition
                  book={book}
                  isActive={isActive}
                  isOpen={isDetailsOpen}
                  onOpen={() => handleBookClick(isActive)}
                  height={bookHeights[book.work_urn]}
                  spineColor={spineColours[book.work_urn]}
                  isMobile={true}
                />
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {activeBook && !isDetailsOpen && (
        <div className="text-center mt-6 transition-opacity duration-300 book-carosel-details">
          <p className="book-title-carosel">{activeBook.title}</p>
          <p className="book-author-carosel">{activeBook.author.split(/[;,]/)[0]}</p>
        </div>
      )}

      <BookDetails
        book={activeBook}
        isOpen={isDetailsOpen}
        onClose={handleToggleDetails}      />
    </>
  );
};

export default MobileBookCarousel;